// src/lib/api/financial/vendorCategoryMappings.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling } from '../shared/errors'

/**
 * Get the suggested category for a vendor name.
 * Returns the category_id with the highest usage_count (case-insensitive match).
 * Returns null if no mapping exists.
 */
const _getSuggestedCategory = async (vendorName) => {
  if (!vendorName || !vendorName.trim()) return null

  const companyId = await getValidatedCompanyId()
  if (!companyId) return null

  const { data, error } = await supabase
    .from('vendor_category_mappings')
    .select('category_id, usage_count')
    .eq('company_id', companyId)
    .ilike('vendor_name', vendorName.trim())
    .order('usage_count', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error

  return data?.category_id || null
}

/**
 * Record a vendor-to-category mapping.
 * Uses upsert: if the mapping already exists, increment usage_count and update last_used_at.
 * If new, create with usage_count=1.
 */
const _recordVendorCategoryMapping = async (vendorName, categoryId) => {
  if (!vendorName || !vendorName.trim() || !categoryId) return null

  const companyId = await getValidatedCompanyId()
  if (!companyId) return null

  const { data, error } = await supabase
    .from('vendor_category_mappings')
    .upsert(
      {
        company_id: companyId,
        vendor_name: vendorName.trim().toLowerCase(),
        category_id: categoryId,
        usage_count: 1,
        last_used_at: new Date().toISOString()
      },
      {
        onConflict: 'company_id,vendor_name,category_id',
        ignoreDuplicates: false
      }
    )
    .select('id, usage_count')
    .single()

  // If the upsert matched an existing row, we need to increment usage_count
  // Supabase upsert replaces the row, so we do a follow-up update to increment
  if (!error && data?.id && data.usage_count >= 1) {
    // Use RPC or a manual increment since upsert doesn't support expressions
    await supabase
      .from('vendor_category_mappings')
      .update({
        usage_count: data.usage_count + 1,
        last_used_at: new Date().toISOString()
      })
      .eq('id', data.id)
      .eq('company_id', companyId)
  }

  if (error) throw error
  return data
}

// Export wrapped functions
export const getSuggestedCategory = withErrorHandling(
  _getSuggestedCategory,
  'get suggested category',
  'vendor_category_mapping'
)

export const recordVendorCategoryMapping = withErrorHandling(
  _recordVendorCategoryMapping,
  'record vendor category mapping',
  'vendor_category_mapping'
)
